import { useState, useRef, useEffect } from 'react';
import './Examples.css';

interface ExamplesProps {
  onSelect: (code: string) => void;
}

interface Example {
  name: string;
  code: string;
}

const EXAMPLES: Example[] = [
  {
    name: 'Hello World',
    code: `// Bienvenue dans Jertscript v2! 🥐
// Now with TypeScript types!

croissant message: texte = "Bonjour, le monde!";
dire(message);

// Variables with types
baguette count: nombre = 0;
pendant (count < 3) {
  dire("Vive la France!");
  count = count + 1;
}

// Typed function
fonction add(a: nombre, b: nombre): nombre {
  retourner a + b;
}

dire(add(2, 3));`,
  },
  {
    name: 'Classes with Types',
    code: `// Classes in Jertscript v2
// With full TypeScript type support

interface Personne {
  nom: texte;
  age: nombre;
  saluer(): vide;
}

classe Francais implemente Personne {
  publique nom: texte;
  publique age: nombre;
  prive humeur: texte;

  constructeur(nom: texte, age: nombre) {
    ceci.nom = nom;
    ceci.age = age;
    ceci.humeur = "magnifique";
  }

  publique saluer(): vide {
    dire("Bonjour! Je suis " + ceci.nom);
  }
}

croissant jean: Personne = nouveau Francais("Jean", 42);
jean.saluer();`,
  },
  {
    name: 'XION Wallet',
    code: `// XION Blockchain Integration
// Connect wallet and check balance

asynchrone fonction demoPortefeuille(): Promesse<vide> {
  dire("Connecting to XION...");

  croissant wallet = attendre connecter();
  dire("Connected! Address: " + wallet.address);

  croissant balance = attendre solde();
  dire("Balance: " + balance.amount + " " + balance.denom);

  attendre deconnecter();
  dire("Disconnected. Au revoir!");
}

demoPortefeuille();`,
  },
  {
    name: 'Types & Interfaces',
    code: `// Type System in Jertscript v2
// Interfaces, type aliases, and generics

interface Point {
  x: nombre;
  y: nombre;
}

type Coordonnees = Point;

interface Cercle etend Point {
  rayon: nombre;
}

fonction distance(p1: Point, p2: Point): nombre {
  croissant dx: nombre = p2.x - p1.x;
  croissant dy: nombre = p2.y - p1.y;
  retourner Math.sqrt(dx * dx + dy * dy);
}

croissant origine: Point = { x: 0, y: 0 };
croissant point: Point = { x: 3, y: 4 };

dire("Distance: " + distance(origine, point));`,
  },
];

function Examples({ onSelect }: ExamplesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = (example: Example) => {
    onSelect(example.code);
    setIsOpen(false);
  };

  return (
    <div className="examples" ref={dropdownRef}>
      <button
        className="examples-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="examples-icon">&#9776;</span>
        Menu du Jour
        <span className={`examples-chevron ${isOpen ? 'open' : ''}`}>&#9662;</span>
      </button>

      {isOpen && (
        <div className="examples-dropdown" role="listbox">
          <div className="examples-header">~ Prix Fixe ~</div>
          <div className="examples-list">
            {EXAMPLES.map((example, index) => (
              <button
                key={index}
                className="examples-item"
                onClick={() => handleSelect(example)}
                role="option"
              >
                <span className="example-number">{index + 1}.</span>
                <span className="example-name">{example.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Examples;
